import { checkForNavigationByCategory } from "@/routing/guards/checkForNavigationByCategory";
import { createRouter, createWebHistory } from "vue-router";
import { DASHBOARD_WIZARD, DASHBOARD_DEFAULT, PUBLIC_WIZARD } from "@/core/constants/layout";
import { routes } from "@/routing/routes";
import { scrollTop } from "@/assets/js/components/util";
import { setDashboardLayout } from "@/core/services/layout.service";
import * as userConstants from "@/core/models/usersAndPermissions";
import catalogRouter from "@/apps/dashboard/catalog/routing/router";
import paymentRouter from "@/apps/dashboard/payment/routing/router";
import identificationService from "@/core/services/identification.service";
import itemRefurbStepRouter from "@/apps/dashboard/itemRefurbStep/routing/itemRefurbStepRouter";
import loginService from "@/core/services/login.service";
import ProductsLayout from "@/apps/dashboard/catalog/view/pages/Product/ProductsPage.vue";
import refurbDiagnosisRouter from "@/apps/dashboard/refurbDiagnosis/routing/router";
import itemLocationRouter from "@/apps/dashboard/itemLocation/routing/router";
import transportRouter from "@/apps/dashboard/transport/routing/router";
import userRouter from "@/apps/dashboard/user/routing/router";
import organizationRouter from "@/apps/dashboard/organization/routing/router";
import warehouseDispatchRouter from "@/apps/dashboard/warehouseDispatch/routing/warehouseDispatchRouter";

const routerDashboard = createRouter({
  history: createWebHistory(),
  mode: "history",
  routes: [
    {
      path: "/auth",
      redirect: routes.login.path,
      component: () => import(/* webpackChunkName: "db-login-main" */ "@/view/layout/LoginLayout.vue"),
      children: [
        {
          path: routes.login.path,
          name: routes.login.name,
          component: () => import(/* webpackChunkName: "db-login" */ "@/view/pages/Identification/Login.vue"),
        },
        {
          path: routes.openIdLogin.path,
          alias: routes.openIdLogin.alias,
          name: routes.openIdLogin.name,
          component: () =>
            import(/* webpackChunkName: "db-login-openid" */ "@/view/pages/Identification/OpenIdLoginCallback.vue"),
          props: true,
        },
        {
          path: routes.openIdLogout.path,
          alias: routes.openIdLogout.alias,
          name: routes.openIdLogout.name,
          beforeEnter: () => loginService.logout(),
          redirect: routes.login.path,
        },
      ],
    },
    {
      path: "/",
      redirect: routes.home.path,
      component: () => import("@/view/layout/Layout.vue"),
      children: [
        {
          path: routes.designSystem.path,
          name: routes.designSystem.name,
          component: () => import(/* webpackChunkName: "design-system" */ "@/designSystem/layout/DesignSystem.vue"),
        },
        {
          path: routes.home.path,
          name: routes.home.name,
          component: () => import(/* webpackChunkName: "db-home" */ "@/view/pages/HomeDashboard.vue"),
          meta: {
            layoutType: DASHBOARD_DEFAULT,
          },
        },
        {
          path: routes.categories.path,
          name: routes.categories.name,
          component: () =>
            import(
              /* webpackChunkName: "db-categories" */ "@/apps/dashboard/catalog/view/pages/Category/Categories.vue"
            ),
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
          meta: {
            permission: userConstants.ROLE_CATEGORY_LIST_PAGE,
          },
        },
        {
          path: routes.editCategory.path,
          name: routes.editCategory.name,
          component: () =>
            import(/* webpackChunkName: "db-categories" */ "@/apps/dashboard/catalog/view/pages/Category/Category.vue"),
          meta: {
            permission: userConstants.ROLE_CATEGORY_UPDATE_PAGE,
          },
        },
        {
          path: routes.createCategory.path,
          name: routes.createCategory.name,
          component: () =>
            import(/* webpackChunkName: "db-categories" */ "@/apps/dashboard/catalog/view/pages/Category/Category.vue"),
          meta: {
            permission: userConstants.ROLE_CATEGORY_CREATE_PAGE,
          },
        },
        {
          path: routes.products.path,
          name: routes.products.name,
          component: ProductsLayout,
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
          meta: {
            permission: userConstants.ROLE_PRODUCT_LIST_PAGE,
          },
        },
        {
          path: routes.createProduct.path,
          name: routes.createProduct.name,
          component: () =>
            import(/* webpackChunkName: "db-products" */ "@/apps/dashboard/catalog/view/pages/Product/Product.vue"),
          meta: {
            permission: userConstants.ROLE_PRODUCT_CREATE_PAGE,
          },
        },
        {
          path: routes.editProduct.path,
          name: routes.editProduct.name,
          component: () =>
            import(/* webpackChunkName: "db-products" */ "@/apps/dashboard/catalog/view/pages/Product/Product.vue"),
          meta: {
            permission: userConstants.ROLE_PRODUCT_UPDATE_PAGE,
          },
        },
        {
          path: routes.diagnostic.path,
          component: () =>
            import(
              /* webpackChunkName: "db-diag" */ "@/apps/dashboard/buybackDiagnostic/view/pages/Question/DiagnosticQuestion.vue"
            ),
          children: [
            {
              path: "",
              name: routes.diagnostic.name,
              component: () =>
                import(
                  /* webpackChunkName: "db-diag" */ "@/apps/dashboard/buybackDiagnostic/view/components/Question/Landing.vue"
                ),
              meta: {
                permission: userConstants.ROLE_QUESTION_LIST_PAGE,
                routeKeyToPreventReloadMainLayout: "diagnostic",
              },
            },
            {
              path: routes.diagnosticCategory.path,
              name: routes.diagnosticCategory.name,
              component: () =>
                import(
                  /* webpackChunkName: "db-diag" */ "@/apps/dashboard/buybackDiagnostic/view/pages/Question/QuestionsList.vue"
                ),
              props: (route) => ({
                alert: route.query.alert ? JSON.parse(route.query.alert) : null,
                categoryId: route.params.categoryId,
              }),
              meta: {
                permission: userConstants.ROLE_QUESTION_LIST_PAGE,
                routeKeyToPreventReloadMainLayout: "diagnostic",
              },
            },
            {
              path: routes.addDiagnosticQuestion.path,
              name: routes.addDiagnosticQuestion.name,
              component: () =>
                import(
                  /* webpackChunkName: "db-diag" */ "@/apps/dashboard/buybackDiagnostic/view/pages/Question/EditAddQuestion.vue"
                ),
              props: (route) => ({
                questionsCount: route.query?.questionsCount,
                categoryId: route.params.categoryId,
              }),
              meta: {
                permission: userConstants.ROLE_QUESTION_UPDATE_PAGE,
                routeKeyToPreventReloadMainLayout: "diagnostic",
              },
            },
            {
              path: routes.editDiagnosticQuestion.path,
              name: routes.editDiagnosticQuestion.name,
              component: () =>
                import(
                  /* webpackChunkName: "db-diag" */ "@/apps/dashboard/buybackDiagnostic/view/pages/Question/EditAddQuestion.vue"
                ),
              props: (route) => ({
                categoryId: route.params.categoryId,
                questionId: route.params.questionId,
                question: route.query?.question ? JSON.parse(route.query.question) : null,
              }),
              meta: {
                permission: userConstants.ROLE_QUESTION_UPDATE_PAGE,
                routeKeyToPreventReloadMainLayout: "diagnostic",
              },
            },
          ],
        },
        ...refurbDiagnosisRouter,
        {
          path: routes.buybacks.path,
          name: routes.buybacks.name,
          component: () =>
            import(
              /* webpackChunkName: "db-buybacks" */ "@/apps/dashboard/buyback/view/pages/BuybacksLists/Buybacks.vue"
            ),
          meta: {
            permission: userConstants.ROLE_BUYBACK_LIST_PAGE,
          },
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
        },
        {
          path: routes.buybacks_predeclared.path,
          name: routes.buybacks_predeclared.name,
          component: () =>
            import(
              /* webpackChunkName: "db-bb-pre" */ "@/apps/dashboard/buyback/view/pages/BuybackPredeclaredCheck.vue"
            ),
          meta: {
            permission: userConstants.ROLE_BUYBACK_PREDECLARED_CHECK_PAGE,
          },
        },
        {
          path: routes.buybacksQuarantine.path,
          name: routes.buybacksQuarantine.name,
          component: () =>
            import(
              /* webpackChunkName: "db-bb-quar" */ "@/apps/dashboard/buyback/view/pages/BuybacksLists/BuybacksQuarantine.vue"
            ),
          meta: {
            permission: userConstants.ROLE_BUYBACKITEM_QUARANTINE_TRANSITION_PAGE,
          },
        },
        {
          path: routes.buybackSearch.path,
          name: routes.buybackSearch.name,
          component: () =>
            import(/* webpackChunkName: "db-bb-create" */ "@/apps/dashboard/buyback/view/pages/BuybackCreate.vue"),
          meta: {
            permission: userConstants.ROLE_BUYBACK_CREATE_SEARCH_PAGE,
            layoutType: DASHBOARD_WIZARD,
          },
          children: [
            {
              path: routes.buybackSearchByCategories.path.replace("/", ""),
              name: routes.buybackSearchByCategories.name,
              component: () =>
                import(/* webpackChunkName: "db-bb-create" */ "@/apps/dashboard/buyback/view/pages/BuybackCreate.vue"),
              meta: {
                permission: userConstants.ROLE_BUYBACK_CREATE_SEARCH_PAGE,
                layoutType: DASHBOARD_WIZARD,
              },
              beforeEnter: () => {
                if (!checkForNavigationByCategory()) {
                  return { name: routes.notFound.name };
                }
              },
            },
            {
              path: routes.buybackSearchCategoryList.path.replace("/", ""),
              name: routes.buybackSearchCategoryList.name,
              component: () =>
                import(/* webpackChunkName: "db-bb-create" */ "@/apps/dashboard/buyback/view/pages/BuybackCreate.vue"),
              meta: {
                permission: userConstants.ROLE_BUYBACK_CREATE_SEARCH_PAGE,
                layoutType: DASHBOARD_WIZARD,
              },
            },
          ],
        },
        {
          path: routes.buybackZero.path,
          name: routes.buybackZero.name,
          component: () =>
            import(
              /* webpackChunkName: "db-bb-zero" */ "@/apps/shared/buyback/view/pages/BuybackWizard/BuybackZero.vue"
            ),
          meta: {
            layoutType: DASHBOARD_WIZARD,
          },
        },
        {
          path: routes.buybackCreate.path,
          name: routes.buybackCreate.name,
          component: () =>
            import(/* webpackChunkName: "db-bb-create" */ "@/apps/dashboard/buyback/view/pages/BuybackCreate.vue"),
          meta: {
            permission: userConstants.ROLE_BUYBACK_CREATE_PAGE,
            layoutType: DASHBOARD_WIZARD,
          },
          props: (route) => ({
            selectedProductId: route.params.selectedProductId,
            selectedProductSlug: route.params.selectedProductSlug,
            predeclaredBuybackId: route.params?.predeclaredBuybackId,
            itemId: route.params?.itemId,
            goToCartFromOtherRoute: route.query?.goToCartFromOtherRoute,
            fromPath: route.query?.fromPath,
          }),
        },
        {
          path: routes.detailBuyback.path,
          name: routes.detailBuyback.name,
          component: () =>
            import(
              /* webpackChunkName: "db-bb-details" */ "@/apps/dashboard/buyback/view/pages/BuybackDetails/BuybackDetails.vue"
            ),
          meta: {
            permission: userConstants.ROLE_BUYBACK_DETAIL_PAGE,
          },
        },
        {
          path: routes.buybackConfirmationDocs.path,
          name: routes.buybackConfirmationDocs.name,
          component: () =>
            import(
              /* webpackChunkName: "db-conf-docs" */ "@/apps/shared/buyback/view/pages/BuybackConfirmation/BuybackCreationConfirmationDocs.vue"
            ),
          props: (route) => ({ buybackIdsQuery: route.query.ids }),
          meta: {
            layoutType: DASHBOARD_WIZARD,
            routeKeyToPreventReloadMainLayout: "diagnostic",
          },
        },
        {
          path: routes.buybackEdition.path,
          name: routes.buybackEdition.name,
          component: () =>
            import(
              /* webpackChunkName: "db-edit-conf" */ "@/view/pages/Warehouse/CompliancyValidation/BuybackEditionConfirmation.vue"
            ),
          props: (route) => ({
            id: route.params.id,
            itemWorkflowStep: route.query.itemWorkflowStep,
          }),
          meta: {
            layoutType: DASHBOARD_WIZARD,
            routeKeyToPreventReloadMainLayout: "diagnostic",
          },
        },
        {
          path: routes.complianceTest.path,
          name: routes.complianceTest.name,
          component: () =>
            import(/* webpackChunkName: "db-wh-wiz" */ "@/view/pages/Warehouse/ComplianceTest/ComplianceTestInit.vue"),
          meta: {
            permission: userConstants.ROLE_BUYBACKITEM_CONTROL_PAGE,
          },
          props: (route) => ({
            itemId: route.params?.itemId,
          }),
        },
        ...transportRouter,
        {
          path: routes.circuits.path,
          name: routes.circuits.name,
          component: () =>
            import(/* webpackChunkName: "db-circuits" */ "@/view/pages/Configuration/Circuits/Circuits.vue"),
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
          meta: {
            permission: userConstants.ROLE_DISPATCH_CIRCUIT_RULE_LIST_PAGE,
          },
        },
        {
          path: routes.editCircuit.path,
          name: routes.editCircuit.name,
          component: () =>
            import(/* webpackChunkName: "db-circuits" */ "@/view/pages/Configuration/Circuits/Circuit.vue"),
          meta: {
            permission: userConstants.ROLE_DISPATCH_CIRCUIT_RULE_EDIT_PAGE,
          },
        },
        {
          path: routes.users.path,
          name: routes.users.name,
          component: () => import(/* webpackChunkName: "db-users" */ "@/view/pages/Configuration/Users/Users.vue"),
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
          meta: {
            permission: userConstants.ROLE_USER_LIST_PAGE,
          },
        },
        {
          path: routes.createUserClient.path,
          name: routes.createUserClient.name,
          component: () =>
            import(/* webpackChunkName: "db-user-client" */ "@/view/pages/Configuration/Users/UserClient.vue"),
          meta: {
            permission: userConstants.ROLE_USER_CREATE_PAGE,
          },
        },
        {
          path: routes.editUserClient.path,
          name: routes.editUserClient.name,
          component: () =>
            import(/* webpackChunkName: "db-user-client" */ "@/view/pages/Configuration/Users/UserClient.vue"),
          meta: {
            permission: userConstants.ROLE_USER_UPDATE_PAGE,
          },
        },
        {
          path: routes.createAdmin.path,
          name: routes.createAdmin.name,
          component: () =>
            import(/* webpackChunkName: "db-user-admin" */ "@/view/pages/Configuration/Users/UserAdmin.vue"),
          meta: {
            permission: userConstants.ROLE_USER_CREATE_ADMIN,
          },
        },
        {
          path: routes.editAdmin.path,
          name: routes.editAdmin.name,
          component: () =>
            import(/* webpackChunkName: "db-user-admin" */ "@/view/pages/Configuration/Users/UserAdmin.vue"),
          meta: {
            permission: userConstants.ROLE_USER_CREATE_ADMIN,
          },
        },
        {
          path: routes.entities.path,
          name: routes.entities.name,
          component: () =>
            import(/* webpackChunkName: "db-entities" */ "@/view/pages/Configuration/Entities/Entities.vue"),
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
          meta: {
            permission: userConstants.ROLE_ORGANIZATION_LIST_PAGE,
          },
        },
        {
          path: routes.editEntity.path,
          name: routes.editEntity.name,
          component: () =>
            import(/* webpackChunkName: "db-entities" */ "@/view/pages/Configuration/Entities/Entity.vue"),
          meta: {
            permission: userConstants.ROLE_ORGANIZATION_UPDATE_PAGE,
          },
          props: true,
        },
        {
          path: routes.createEntity.path,
          name: routes.createEntity.name,
          component: () =>
            import(/* webpackChunkName: "db-entities" */ "@/view/pages/Configuration/Entities/Entity.vue"),
          meta: {
            permission: userConstants.ROLE_ORGANIZATION_CREATE_PAGE,
          },
        },
        {
          path: routes.design.path,
          name: routes.design.name,
          component: () => import(/* webpackChunkName: "db-design" */ "@/view/pages/Configuration/Design.vue"),
          meta: {
            permission: userConstants.ROLE_DESIGN_PAGE,
          },
        },
        {
          path: routes.lcmRpsGeneralSettings.path,
          name: routes.lcmRpsGeneralSettings.name,
          component: () =>
            import(
              /* webpackChunkName: "db-settings-lcmrps" */ "@/view/pages/Configuration/Settings/LcmRpsGeneralSettings.vue"
            ),
          meta: {
            permission: userConstants.ROLE_GENERAL_SETTINGS_PAGE,
          },
        },
        {
          path: routes.timGeneralSettings.path,
          name: routes.timGeneralSettings.name,
          component: () =>
            import(
              /* webpackChunkName: "db-settings-tim" */ "@/view/pages/Configuration/Settings/TimGeneralSettings.vue"
            ),
          meta: {
            permission: userConstants.ROLE_GENERAL_SETTINGS_PAGE,
          },
        },
        {
          path: routes.sftpSettings.path,
          name: routes.sftpSettings.name,
          component: () =>
            import(/* webpackChunkName: "db-settings-sftp" */ "@/view/pages/Configuration/Settings/SftpSettings.vue"),
          meta: {
            permission: userConstants.ROLE_GENERAL_SETTINGS_PAGE,
          },
        },
        {
          path: routes.transferOrderSettings.path,
          name: routes.transferOrderSettings.name,
          component: () =>
            import(
              /* webpackChunkName: "db-settings-to" */ "@/view/pages/Configuration/Settings/TransferOrderSettings.vue"
            ),
          meta: {
            permission: userConstants.ROLE_TRANSFER_ORDER_SETTINGS_PAGE,
          },
        },
        {
          path: routes.clients.path,
          name: routes.clients.name,
          component: () =>
            import(/* webpackChunkName: "db-clients" */ "@/view/pages/Configuration/Clients/Clients.vue"),
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
          meta: {
            permission: userConstants.ROLE_CLIENT_LIST_PAGE,
          },
        },
        {
          path: routes.createClient.path,
          name: routes.createClient.name,
          component: () => import(/* webpackChunkName: "db-clients" */ "@/view/pages/Configuration/Clients/Client.vue"),
          meta: {
            permission: userConstants.ROLE_CLIENT_CREATE_PAGE,
          },
        },
        {
          path: routes.editClient.path,
          name: routes.editClient.name,
          component: () => import(/* webpackChunkName: "db-clients" */ "@/view/pages/Configuration/Clients/Client.vue"),
          props: (route) => ({
            organizationId: route.params.organizationId,
            name: route.query?.name,
          }),
          meta: {
            permission: userConstants.ROLE_CLIENT_UPDATE_PAGE,
          },
        },
        {
          path: routes.editClientPages.path,
          name: routes.editClientPages.name,
          component: () =>
            import(/* webpackChunkName: "db-cms" */ "@/view/pages/Configuration/CustomPages/HomePublicConfig.vue"),
          meta: {
            permission: userConstants.ROLE_CLIENT_EDIT_CMS_PAGES_PAGE,
          },
          props: (route) => ({
            organizationId: route.params.organizationId,
            name: route.query?.name,
          }),
        },
        {
          path: routes.connectorListConnectors.path,
          name: routes.connectorListConnectors.name,
          component: () =>
            import(/* webpackChunkName: "db-connect-list" */ "@/view/pages/Configuration/Connectors/ConnectorList.vue"),
          props: (route) => ({
            organizationId: route.params.organizationId,
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
            organizationName: route.query?.organizationName,
            connectorId: route.query?.connectorId,
            connectorName: route.query?.connectorName,
          }),
          meta: {
            permission: userConstants.ADMIN_CIRCULARX,
          },
        },
        {
          path: routes.ssoConfiguration.path,
          name: routes.ssoConfiguration.name,
          component: () =>
            import(/* webpackChunkName: "db-sso" */ "@/view/pages/Configuration/Connectors/SsoConfiguration.vue"),
          meta: {
            permission: userConstants.ADMIN_CIRCULARX,
          },
        },
        {
          path: routes.configurationInternalSftp.path,
          name: routes.configurationInternalSftp.name,
          component: () =>
            import(/* webpackChunkName: "db-config" */ "@/view/pages/Configuration/CustomPages/InternalSftpConfig.vue"),
          meta: {
            permission: userConstants.ADMIN_CIRCULARX,
          },
        },
        {
          path: routes.configurationListConnectors.path,
          name: routes.configurationListConnectors.name,
          component: () =>
            import(
              /* webpackChunkName: "db-config-list" */ "@/view/pages/Configuration/Connectors/ConfigurationList.vue"
            ),
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
            organizationId: route.params.organizationId,
            connectorId: route.params.connectorId,
            organizationName: route.query?.organizationName,
            connectorName: route.query?.connectorName,
          }),
          meta: {
            permission: userConstants.ADMIN_CIRCULARX,
          },
        },
        {
          path: routes.configuration.path,
          name: routes.configuration.name,
          component: () =>
            import(/* webpackChunkName: "db-config" */ "@/view/pages/Configuration/Connectors/Configuration.vue"),
          meta: {
            permission: userConstants.ADMIN_CIRCULARX,
          },
          props: (route) => ({
            configurationId: route.params.configurationId,
            organizationId: route.query?.organizationId,
            connectorId: route.query?.connectorId,
            organizationName: route.query?.organizationName,
            connectorName: route.query?.connectorName,
          }),
        },
        {
          path: routes.createConfiguration.path,
          name: routes.createConfiguration.name,
          component: () =>
            import(/* webpackChunkName: "db-config" */ "@/view/pages/Configuration/Connectors/Configuration.vue"),
          meta: {
            permission: userConstants.ADMIN_CIRCULARX,
          },
          props: (route) => ({
            organizationId: route.query?.organizationId,
            connectorId: route.query?.connectorId,
            organizationName: route.query?.organizationName,
            connectorName: route.query?.connectorName,
          }),
        },
        {
          path: routes.lostPassword.path,
          name: routes.lostPassword.name,
          component: () => import(/* webpackChunkName: "db-pw-lost" */ "@/view/pages/Identification/LostPassword.vue"),
          meta: {
            layoutType: PUBLIC_WIZARD,
          },
        },
        {
          path: routes.passwordRenewal.path,
          name: routes.passwordRenewal.name,
          component: () => import(/* webpackChunkName: "db-pw" */ "@/view/pages/Identification/PasswordRenewal.vue"),
          meta: {
            layoutType: PUBLIC_WIZARD,
          },
        },
        {
          path: routes.passwordConfirmation.path,
          name: routes.passwordConfirmation.name,
          component: () =>
            import(/* webpackChunkName: "db-pw-conf" */ "@/view/pages/Identification/PasswordConfirmation.vue"),
          props: (route) => ({
            isPasswordCreation: route.query.isPasswordCreation,
          }),
          meta: {
            layoutType: PUBLIC_WIZARD,
          },
        },
        ...warehouseDispatchRouter,
        {
          path: routes.refurbSteps.path,
          name: routes.refurbSteps.name,
          component: () =>
            import(/* webpackChunkName: "refurb-steps" */ "@/view/pages/RefurbSteps/RefurbStepsList.vue"),
          props: (route) => ({
            alert: route.query.alert ? JSON.parse(route.query.alert) : null,
          }),
          meta: {
            permission: userConstants.ROLE_REFURB_STEP_MANAGE,
            routeKeyToPreventReloadMainLayout: "refurbStep",
          },
        },
        {
          path: routes.createRefurbStep.path,
          name: routes.createRefurbStep.name,
          component: () => import(/* webpackChunkName: "refurb-steps" */ "@/view/pages/RefurbSteps/RefurbStep.vue"),
          meta: {
            permission: userConstants.ROLE_REFURB_STEP_MANAGE,
            routeKeyToPreventReloadMainLayout: "refurbStep",
          },
          props: true,
        },
        {
          path: routes.editRefurbStep.path,
          name: routes.editRefurbStep.name,
          component: () => import(/* webpackChunkName: "refurb-steps" */ "@/view/pages/RefurbSteps/RefurbStep.vue"),
          meta: {
            permission: userConstants.ROLE_REFURB_STEP_MANAGE,
            routeKeyToPreventReloadMainLayout: "refurbStep",
          },
          props: true,
        },
        ...itemLocationRouter,
        ...itemRefurbStepRouter,
        {
          ...routes.imports,
          component: () =>
            import(/* webpackChunkName: "import" */ "@/apps/dashboard/catalog/view/pages/Import/Imports.vue"),
          meta: {
            permission: userConstants.ROLE_IMPORTS_PAGE,
          },
          props: (route) => ({
            organizationId: route.params.organizationId,
            organizationName: route.query?.organizationName,
          }),
        },
        {
          path: routes.cartCustomerInformations.path,
          name: routes.cartCustomerInformations.name,
          component: () =>
            import(
              /* webpackChunkName: "web" */ "@/apps/dashboard/buyback/view/pages/CustomerInformations/CustomerInformations.vue"
            ),
          meta: {
            permission: userConstants.ROLE_BUYBACK_CREATE_SEARCH_PAGE,
            layoutType: DASHBOARD_WIZARD,
            routeKeyToPreventReloadMainLayout: "customerInformations",
          },
        },
        {
          path: routes.cartOrderSummary.path,
          name: routes.cartOrderSummary.name,
          component: () =>
            import(/* webpackChunkName: "web" */ "@/apps/dashboard/buyback/view/pages/OrderSummary/OrderSummary.vue"),
          meta: {
            permission: userConstants.ROLE_BUYBACK_CREATE_SEARCH_PAGE,
            layoutType: DASHBOARD_WIZARD,
            routeKeyToPreventReloadMainLayout: "orderSummary",
          },
        },
        {
          path: routes.accessDenied.path,
          name: routes.accessDenied.name,
          component: () => import(/* webpackChunkName: "error-access-denied" */ "@/view/pages/Error/AccessDenied.vue"),
        },
        ...catalogRouter,
        ...organizationRouter,
        ...userRouter,
        ...paymentRouter,
      ],
    },
    {
      path: routes.catchAll.path,
      name: routes.catchAll.name,
      redirect: routes.notFound.path,
    },
    {
      path: routes.notFound.path,
      name: routes.notFound.name,
      component: () => import(/* webpackChunkName: "db-404" */ "@/view/pages/Error/Error-1.vue"),
    },
  ],
});

const dashboardPublicRoutes = [
  routes.login.name,
  routes.lostPassword.name,
  routes.passwordRenewal.name,
  routes.passwordConfirmation.name,
  routes.openIdLogin.name,
];

routerDashboard.beforeEach((to) => {
  if (!identificationService.getToken() && !dashboardPublicRoutes.includes(to.name)) {
    return { name: routes.login.name };
  }

  if (to.meta.permission && !identificationService.hasPermission(to.meta.permission)) {
    return { name: routes.home.name };
  }

  return true;
});

routerDashboard.afterEach((to, from) => {
  if (to.path !== from.path) {
    setDashboardLayout(to.meta.layoutType);
  }

  scrollTop();
});

export default routerDashboard;
